import React, { useEffect, useState, useCallback } from 'react';
import {
  AnalyticalTable,
  Button,
  Dialog,
  Title,
  BusyIndicator,
  Input,
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Label
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import useAutoTranslate from '../hooks/useAutoTranslate';
import {
  buildSaleChannelWritePayload,
  validateSaleChannelForm,
  SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH
} from '../services/saleChannels';

/** API camelCase (`externalCode`); tolera snake_case del backend. */
function pickSaleChannelExternalCode(row) {
  if (!row) return '';
  const v = row.externalCode ?? row.external_code;
  return v != null ? String(v).trim() : '';
}

const SaleChannelList = () => {
  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ sortBy: 'name', sortDirection: 'asc' });

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const [formData, setFormData] = useState({ name: '', costCenter: '', externalCode: '' });
  const [formErrors, setFormErrors] = useState({});

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchSaleChannels = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`sale-channels?page=${page}&size=${pageSize}`);
      if (response.data.records.code === 'SUCCESS') {
        setData(response.data.records.data);
        setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError('Error al cargar los canales de venta: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => { fetchSaleChannels(currentPage); }, [currentPage, fetchSaleChannels]);

  // ── Filtrado / Ordenamiento ────────────────────────────────────────────────

  useEffect(() => {
    let filtered = [...data];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        (c.name || '').toLowerCase().includes(term) ||
        (c.costCenter || '').toLowerCase().includes(term) ||
        pickSaleChannelExternalCode(c).toLowerCase().includes(term)
      );
    }
    filtered.sort((a, b) => {
      const vA =
        sortConfig.sortBy === 'externalCode'
          ? pickSaleChannelExternalCode(a)
          : a[sortConfig.sortBy] || '';
      const vB =
        sortConfig.sortBy === 'externalCode'
          ? pickSaleChannelExternalCode(b)
          : b[sortConfig.sortBy] || '';
      const cmp = typeof vA === 'string'
        ? vA.localeCompare(vB)
        : vA - vB;
      return sortConfig.sortDirection === 'asc' ? cmp : -cmp;
    });
    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  const handleSort = (col) =>
    setSortConfig(prev => ({
      sortBy: col,
      sortDirection: prev.sortBy === col && prev.sortDirection === 'asc' ? 'desc' : 'asc'
    }));

  // ── Paginación ─────────────────────────────────────────────────────────────

  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(p => p - 1); };
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(p => p + 1); };

  // ── Formulario ─────────────────────────────────────────────────────────────

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = validateSaleChannelForm(formData, autoT);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Crear ──────────────────────────────────────────────────────────────────

  const handleCreateClick = () => {
    if (!hasPermission('saleschannel', 'create')) return;
    setFormData({ name: '', costCenter: '', externalCode: '' });
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;
    try {
      const response = await axiosInstance.post('sale-channels', buildSaleChannelWritePayload(formData));
      if (response.status === 200 || response.status === 201) {
        fetchSaleChannels(currentPage);
        setShowCreateDialog(false);
      } else {
        setError('Error al crear el canal de venta: ' + (response.data?.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al crear el canal de venta: ' + (err.message || 'Error desconocido'));
    }
  };

  // ── Editar ─────────────────────────────────────────────────────────────────

  const handleRowClick = (event) => {
    if (!hasPermission('saleschannel', 'update')) return;
    const { detail } = event;
    if (detail?.row?.original) {
      const c = detail.row.original;
      setSelectedChannel(c);
      setFormData({
        name: c.name || '',
        costCenter: c.costCenter || '',
        externalCode: pickSaleChannelExternalCode(c)
      });
      setFormErrors({});
      setShowEditDialog(true);
    }
  };

  const handleEditClick = (channel) => {
    if (!hasPermission('saleschannel', 'update')) return;
    setSelectedChannel(channel);
    setFormData({
      name: channel.name || '',
      costCenter: channel.costCenter || '',
      externalCode: pickSaleChannelExternalCode(channel)
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !selectedChannel) return;
    try {
      const response = await axiosInstance.put(
        `sale-channels/${selectedChannel.id}`,
        buildSaleChannelWritePayload(formData)
      );
      if (response.status === 200) {
        fetchSaleChannels(currentPage);
        setShowEditDialog(false);
      } else {
        setError('Error al actualizar el canal de venta: ' + (response.data?.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al actualizar el canal de venta: ' + (err.message || 'Error desconocido'));
    }
  };

  // ── Eliminar ───────────────────────────────────────────────────────────────

  const handleDeleteClick = (channel) => {
    if (!hasPermission('saleschannel', 'delete')) return;
    setSelectedChannel(channel || selectedChannel);
    setShowDeleteConfirm(true);
    if (showEditDialog) setShowEditDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedChannel) return;
    try {
      await axiosInstance.delete(`sale-channels/${selectedChannel.id}`);
      fetchSaleChannels(currentPage);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Error al eliminar el canal de venta: ' + (err.message || 'Error desconocido'));
    }
  };

  // ── Columnas ───────────────────────────────────────────────────────────────

  const columns = [
    {
      Header: autoT('Nombre'),
      accessor: 'name',
      headerProps: { onClick: () => handleSort('name') },
      className: sortConfig.sortBy === 'name' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: autoT('Centro de Costo'),
      accessor: 'costCenter',
      headerProps: { onClick: () => handleSort('costCenter') },
      className: sortConfig.sortBy === 'costCenter' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ value }) => <Text>{value || '—'}</Text>
    },
    {
      Header: autoT('Código Externo'),
      id: 'externalCode',
      accessor: (row) => pickSaleChannelExternalCode(row),
      headerProps: { onClick: () => handleSort('externalCode') },
      className: sortConfig.sortBy === 'externalCode' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ row }) => <Text>{pickSaleChannelExternalCode(row.original) || '—'}</Text>
    },
    {
      Header: autoT('Acciones'),
      id: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasPermission('saleschannel', 'update') && (
            <Button icon="edit" design="Transparent"
              onClick={(e) => { e.stopPropagation(); handleEditClick(row.original); }} />
          )}
          {hasPermission('saleschannel', 'delete') && (
            <Button icon="delete" design="Transparent"
              onClick={(e) => { e.stopPropagation(); handleDeleteClick(row.original); }} />
          )}
        </div>
      )
    }
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '1.5rem', backgroundColor: 'var(--sapBackgroundColor)', minHeight: '100vh' }}>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de Canales de Venta')}</Title>

        {error && <div style={{ color: 'red', margin: '1rem 0' }}>{error}</div>}

        <Bar
          startContent={
            <Input
              placeholder={autoT('Buscar canal de venta...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '250px' }}
            />
          }
          endContent={
            hasPermission('saleschannel', 'create') && (
              <Button icon="add" design="Emphasized" onClick={handleCreateClick}>
                {autoT('Nuevo Canal de Venta')}
              </Button>
            )
          }
        />

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={filteredData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('saleschannel', 'update') ? handleRowClick : undefined}
              style={{ marginTop: '1rem' }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <Button icon="navigation-left-arrow" disabled={currentPage === 1} onClick={handlePrevPage}>
                {autoT('Anterior')}
              </Button>
              <Text style={{ alignSelf: 'center' }}>
                {autoT('Página')} {currentPage} {autoT('de')} {totalPages}
              </Text>
              <Button icon="navigation-right-arrow" disabled={currentPage === totalPages} onClick={handleNextPage} iconEnd>
                {autoT('Siguiente')}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Dialog Crear */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        header={<Bar startContent={<Title level="H4">{autoT('Crear Nuevo Canal de Venta')}</Title>} />}
        footer={
          <Bar endContent={
            <>
              <Button design="Emphasized" onClick={handleCreateSubmit}>{autoT('Guardar')}</Button>
              <Button design="Transparent" onClick={() => setShowCreateDialog(false)}>{autoT('Cancelar')}</Button>
            </>
          } />
        }
        style={{ width: '600px' }}
      >
        <Form style={{ padding: '1rem' }} backgroundDesign="Transparent"
          columnsXL={1} columnsL={1} columnsM={1} columnsS={1}
          labelSpanXL={4} labelSpanL={4} labelSpanM={12} labelSpanS={12}
        >
          <FormGroup titleText={autoT('Información del Canal de Venta')}>
            <FormItem labelContent={<Label required>{autoT('Nombre')}</Label>}>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                valueState={formErrors.name ? 'Error' : 'None'}
                valueStateMessage={formErrors.name}
                required
                style={{ width: '100%' }}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Centro de Costo')}</Label>}>
              <Input
                name="costCenter"
                value={formData.costCenter}
                onChange={handleInputChange}
                maxlength={SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH}
                valueState={formErrors.costCenter ? 'Error' : 'None'}
                valueStateMessage={formErrors.costCenter}
                style={{ width: '100%' }}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Código Externo')}</Label>}>
              <Input
                name="externalCode"
                value={formData.externalCode}
                onChange={handleInputChange}
                maxlength={SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH}
                valueState={formErrors.externalCode ? 'Error' : 'None'}
                valueStateMessage={formErrors.externalCode}
                style={{ width: '100%' }}
              />
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Dialog Editar */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        header={<Bar startContent={<Title level="H4">{autoT('Editar Canal de Venta')}: {selectedChannel?.name}</Title>} />}
        footer={
          <Bar
            startContent={
              hasPermission('saleschannel', 'delete') && (
                <Button design="Negative" icon="delete" onClick={() => handleDeleteClick(null)}>
                  {autoT('Eliminar')}
                </Button>
              )
            }
            endContent={
              <>
                <Button design="Emphasized" onClick={handleEditSubmit}>{autoT('Guardar Cambios')}</Button>
                <Button design="Transparent" onClick={() => setShowEditDialog(false)}>{autoT('Cancelar')}</Button>
              </>
            }
          />
        }
        style={{ width: '600px' }}
      >
        <Form style={{ padding: '1rem' }} backgroundDesign="Transparent"
          columnsXL={1} columnsL={1} columnsM={1} columnsS={1}
          labelSpanXL={4} labelSpanL={4} labelSpanM={12} labelSpanS={12}
        >
          <FormGroup titleText={autoT('Información del Canal de Venta')}>
            <FormItem labelContent={<Label required>{autoT('Nombre')}</Label>}>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                valueState={formErrors.name ? 'Error' : 'None'}
                valueStateMessage={formErrors.name}
                required
                style={{ width: '100%' }}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Centro de Costo')}</Label>}>
              <Input
                name="costCenter"
                value={formData.costCenter}
                onChange={handleInputChange}
                maxlength={SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH}
                valueState={formErrors.costCenter ? 'Error' : 'None'}
                valueStateMessage={formErrors.costCenter}
                style={{ width: '100%' }}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Código Externo')}</Label>}>
              <Input
                name="externalCode"
                value={formData.externalCode}
                onChange={handleInputChange}
                maxlength={SALE_CHANNEL_EXTERNAL_CODE_MAX_LENGTH}
                valueState={formErrors.externalCode ? 'Error' : 'None'}
                valueStateMessage={formErrors.externalCode}
                style={{ width: '100%' }}
              />
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Confirmación eliminar */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(action) => {
          if (action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        titleText={autoT('Confirmar eliminación')}
        type={MessageBoxType.Warning}
        actions={[MessageBoxAction.OK, MessageBoxAction.Cancel]}
      >
        {autoT('¿Está seguro que desea eliminar el canal de venta')} <strong>{selectedChannel?.name}</strong>? {autoT('Esta acción no se puede deshacer.')}
      </MessageBox>
    </div>
  );
};

export default withPermission(SaleChannelList, 'saleschannel', 'listView');
